import * as React from "react";
import * as CSS from "csstype";

interface Profile {
  id: number;
  first_name: string;
  last_name: string;
  city: string;
  state: string;
  zip: string;
  image: string;
  url: string;
  compensation: string | number;
  job_types: Array<any>;
  skills: Array<any>;
  position_types: Array<any>;
}

interface Job {
  [prop: string]: any;
}

interface InviteProfileProps {
  profile: Profile;
  job: Job;
  requested: Array<number>;
  accepted: Array<number>;
  declined: Array<number>;
  authenticity_token: string;
  callback?: (id: number) => any;
  requestingAll?: boolean;
  processingState?: 'idle' | 'pending' | 'processing' | 'completed';
  fullWidth?: boolean;
}

/**
 * InviteProfile Component
 * 
 * Handles inviting candidates to jobs or requesting temporary workers.
 * Supports both individual and batch processing states with proper loading
 * and status indicators.
 * 
 * Features:
 * - Different UI for temp vs permanent positions
 * - Batch processing support with state indicators
 * - Async invite/request handling with CSRF protection
 * - Status badges for different states
 */
export const InviteProfile: React.FC<InviteProfileProps> = ({
  profile,
  job,
  authenticity_token,
  callback = undefined,
  requested = [],
  accepted = [],
  declined = [],
  requestingAll = false,
  processingState = 'idle',
  fullWidth = false
}) => {
  const [state, setState] = React.useState({
    loading: false,
    requested: requested.indexOf(profile.id) >= 0,
    accepted: accepted.indexOf(profile.id) >= 0,
    declined: declined.indexOf(profile.id) >= 0
  });

  // Helper function to generate button classes
  const getButtonClasses = (additionalClasses: string = '') => {
    const baseClasses = `btn-modern btn-modern--sm ${additionalClasses}`;
    return fullWidth ? `${baseClasses} btn-modern--block` : baseClasses;
  };

  // Helper function to generate button styles
  const getButtonStyles = (additionalStyles: React.CSSProperties = {}) => {
    if (fullWidth) {
      return {
        width: '100%',
        padding: '0.75rem 1rem',
        fontSize: '0.875rem',
        ...additionalStyles
      };
    }
    return additionalStyles;
  };

  React.useEffect(() => {
    setState(prevState => ({
      ...prevState,
      requested: requested.indexOf(profile.id) >= 0,
      accepted: accepted.indexOf(profile.id) >= 0,
      declined: declined.indexOf(profile.id) >= 0
    }));
  }, [requested, accepted, declined, profile.id]);

  const inviteCandidate = () => {
    setState(prevState => ({ ...prevState, loading: true }));

    const url = job.position_type == 'temp'
      ? `/jobs/${job.id}/request.json`
      : `/jobs/${job.id}/invite.json`;

    const options: RequestInit = {
      method: 'POST',
      redirect: 'manual' as RequestRedirect,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': authenticity_token
      },
      body: job.position_type == 'temp'
        ? JSON.stringify({ temp_requests: [profile.id] })
        : JSON.stringify({ invitees: [profile.id] })
    };

    fetch(url, options)
      .then(async result => {
        // Handle session expiration
        if (result.status === 401) {
          const contentType = result.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const data = await result.json();
            if (data.redirect_to) {
              // Store current location to return after login
              sessionStorage.setItem('return_to', window.location.href);
              window.location.href = data.redirect_to;
              return Promise.reject('Session expired - redirecting to login');
            }
          }
          throw new Error('Authentication required');
        }

        if (!result.ok) {
          throw new Error(`HTTP error! status: ${result.status}`);
        }

        const contentType = result.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          return result.json();
        } else {
          return Promise.resolve({ success: true });
        }
      })
      .then(
        result => {
          setState(prevState => ({ ...prevState, loading: false, requested: true }));
          if (callback) callback(profile.id);
        },
        error => {
          setState(prevState => ({ ...prevState, loading: false }));
          console.error('Error inviting candidate:', error);
        }
      );
  };

  // Hide button entirely for accepted profiles
  if (state.accepted) {
    return null;
  }

  // Handle batch processing states first
  if (processingState === 'completed' || state.requested) {
    return (
      <button className={getButtonClasses('btn-modern--success')} disabled style={getButtonStyles()}>
        <i className="fad fa-check"></i> {job.position_type == 'temp' ? "Requested" : "Invited"}
      </button>
    );
  }

  if (processingState === 'processing') {
    return (
      <button className={getButtonClasses('btn-modern--primary')} disabled style={getButtonStyles()}>
        <i className="fad fa-spinner fa-spin"></i> Processing...
      </button>
    );
  }

  if (processingState === 'pending') {
    return (
      <button className={getButtonClasses()} disabled style={getButtonStyles({
        background: 'linear-gradient(135deg, #ffc107, #ffb300)',
        color: '#212529'
      })}>
        <i className="fad fa-clock"></i> Pending...
      </button>
    );
  }

  // Handle individual loading state
  if (state.loading || requestingAll) {
    return (
      <button className={getButtonClasses('btn-modern--primary')} disabled style={getButtonStyles()}>
        <i className="fad fa-spinner fa-spin"></i> {job.position_type == 'temp' ? "Requesting..." : "Inviting..."}
      </button>
    );
  }

  if (state.declined) {
    return (
      <button className={getButtonClasses()} disabled style={getButtonStyles({
        background: '#6c757d',
        color: 'white'
      })}>
        <i className="fad fa-times"></i> {job.position_type == 'temp' ? "Declined" : "Declined"}
      </button>
    );
  }

  // Check if temp position is filled
  if (job.position_type === 'temp' && job.temp_filled) {
    return (
      <button className={getButtonClasses()} disabled style={getButtonStyles({
        background: '#6c757d',
        cursor: 'not-allowed',
        opacity: 0.6
      })}>
        <i className="fad fa-ban"></i> Position Filled
      </button>
    );
  }

  // Check if temp job date has passed (timezone-safe: parse as local date)
  const isTempDatePast = (() => {
    if (job.position_type !== 'temp' || !job.temp_date) return false;
    const dateParts = job.temp_date.toString().split('T')[0].split('-');
    const tempDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return tempDate < today;
  })();

  if (isTempDatePast) {
    return (
      <button className={getButtonClasses()} disabled style={getButtonStyles({
        background: '#6c757d',
        cursor: 'not-allowed',
        opacity: 0.6
      })}>
        <i className="fad fa-calendar-times"></i> Date Passed
      </button>
    );
  }

  return (
    <button
      className={getButtonClasses('btn-modern--primary')}
      style={getButtonStyles()}
      onClick={e => {
        e.stopPropagation();
        inviteCandidate();
      }}
    >
      <i className="fad fa-user-plus"></i> {job.position_type == 'temp' ? "Request Temp" : "Send Invite"}
    </button>
  );
};

export default InviteProfile;

// Styles
const buttonStyle: CSS.Properties = {
  textAlign: "center",
  margin: "12px",
};

const spinnerStyle: CSS.Properties = {
  paddingRight: "15px",
  paddingTop: "10px",
};

const requestedButtonStyle: CSS.Properties = {
  ...buttonStyle,
  background: "#fff",
  color: "black",
  border: "none",
};

const acceptedButtonStyle: CSS.Properties = {
  ...buttonStyle,
  background: "white",
  color: "green",
  border: "none",
};

const declinedButtonStyle: CSS.Properties = {
  ...buttonStyle,
  background: "white",
  color: "#a00606",
  border: "none",
};

const processingStyle: CSS.Properties = {
  backgroundColor: '#186fc9',
  borderColor: '#186fc9',
  color: '#fff'
};

const pendingStyle: CSS.Properties = {
  backgroundColor: '#ffc107',
  borderColor: '#ffc107',
  color: '#212529'
};
